import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  Replay as RetryIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import DataTable from '@/components/DataTable/DataTable';
import { FacilitiesApi } from '@/api/clients/facilities/facilities.api';
import { useGetFacilityGroupsForForms } from '@/api/hooks/facility-groups/useGetFacilityGroupsForForms';
import { TransactionsApi } from '@/api/clients/transactions/transactions.api';
import { useConfirmation } from '@/context/ConfirmationContext';
import { REFUND_METHOD, TRANSACTION_STATUSES } from '@/utility';
import CopyButton from '@/components/DataTable/columns/CopyButton';
import TransactionIdColumn from '@/components/DataTable/columns/TransactionIdColumn';
import TransactionStatusChip from '@/components/TransactionStatusChip';
import EllipsisText from '@/components/EllipsisText';

const Transactions = () => {
  const { t } = useTranslation();
  const { requestConfirmation } = useConfirmation();
  const { facilityGroups } = useGetFacilityGroupsForForms();
  const [actionLoading, setActionLoading] = useState(false);

  const handleTransferBackAction = async (
    transferId,
    isRetry = false,
    refresh,
  ) => {
    const confirmMessage = isRetry
      ? t('transactions.confirmations.retryTransferBack', { transferId })
      : t('transactions.confirmations.createTransferBack', { transferId });

    requestConfirmation(confirmMessage, async () => {
      try {
        setActionLoading(true);
        await TransactionsApi.createTransferBack(transferId);
      } catch (error) {
        console.error(
          `Error ${isRetry ? 'retrying' : 'creating'} transfer back:`,
          error,
        );
      } finally {
        await refresh();
        setActionLoading(false);
      }
    });
  };

  const transfersColumnFilters = useMemo(
    () => [
      {
        key: 'search',
        label: t('common.search'),
        filterType: 'search',
        placeholder: t('transactions.search.placeholder'),
        debounce: true,
        debounceDelay: 500,
      },
      {
        key: 'transaction_status',
        label: t('transactions.filters.status'),
        filterType: 'select',
        options: [
          { value: '', label: t('transactions.filters.allStatuses') },
          {
            value: TRANSACTION_STATUSES.PENDING,
            label: t('transactions.filters.statusPending'),
          },
          {
            value: TRANSACTION_STATUSES.SUCCESS,
            label: t('transactions.filters.statusSuccess'),
          },
          {
            value: TRANSACTION_STATUSES.ERROR,
            label: t('transactions.filters.statusError'),
          },
          {
            value: TRANSACTION_STATUSES.ACH_RETURNED,
            label: t('transactions.filters.statusAchReturned'),
          },
          {
            value: TRANSACTION_STATUSES.SENT_UNCASHED,
            label: t('transactions.filters.statusSentUncashed'),
          },
          {
            value: TRANSACTION_STATUSES.VOIDED,
            label: t('transactions.filters.statusVoided'),
          },
          {
            value: TRANSACTION_STATUSES.ACH_RETURN_PENDING_RETURN_TO_BANK,
            label: t('transactions.filters.statusAchReturnPendingReturnToBank'),
          },
          {
            value: TRANSACTION_STATUSES.ACH_RETURN_AVAILABLE_TO_RECLAIM,
            label: t('transactions.filters.statusAchReturnAvailableToReclaim'),
          },
        ],
      },
      {
        key: 'facility_id',
        label: t('transactions.filters.facility'),
        filterType: 'select',
        loadOptions: () => FacilitiesApi.getFacilities({ facility: true }),
        optionsTransform: (data) => [
          { value: '', label: t('transactions.filters.allFacilities') },
          ...data.map((facility) => ({
            value: facility.id,
            label: facility.name,
          })),
        ],
      },
      {
        key: 'facility_group_id',
        label: t('transactions.filters.facilityGroup'),
        filterType: 'select',
        options: [
          { value: '', label: t('transactions.filters.allFacilityGroups') },
          ...facilityGroups.map((group) => ({
            value: group.id,
            label: `${group.organization_name} - ${group.name}`,
          })),
        ],
      },
      {
        key: 'transaction_type',
        label: t('transactions.filters.transactionType'),
        filterType: 'select',
        options: [
          { value: '', label: t('transactions.filters.allTypes') },
          {
            value: REFUND_METHOD.PAYPAL.toString(),
            label: t('transactions.filters.paypal'),
          },
          {
            value: REFUND_METHOD.VENMO.toString(),
            label: t('transactions.filters.venmo'),
          },
          {
            value: REFUND_METHOD.GIFTCARD.toString(),
            label: t('transactions.filters.giftCard'),
          },
          {
            value: REFUND_METHOD.CHECK.toString(),
            label: t('transactions.filters.check'),
          },
          {
            value: REFUND_METHOD.DONATION.toString(),
            label: t('transactions.filters.donation'),
          },
          {
            value: REFUND_METHOD.ACH.toString(),
            label: t('transactions.filters.ach'),
          },
          {
            value: REFUND_METHOD.TRANSFER_BACK.toString(),
            label: t('transactions.filters.transferBack'),
          },
          {
            value: REFUND_METHOD.CANCEL.toString(),
            label: t('transactions.filters.cancel'),
          },
        ],
      },
    ],
    [t, facilityGroups],
  );

  const transationsColumns = [
    {
      key: 'id',
      label: t('transactions.table.transactionId'),
      sortable: true,
      render: (value) => (
        <TransactionIdColumn
          value={value}
          enableNavigation={true}
          enableCopy={true}
        />
      ),
    },
    {
      key: 'facility_name',
      label: t('transactions.table.facilityName'),
      sortable: true,
      render: (value) => (
        <Typography variant='body2' color='primary' sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      ),
    },
    {
      key: 'refund_id',
      label: t('transactions.table.cpdId'),
      sortable: true,
      render: (value) => (
        <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
          {value}
        </Typography>
      ),
    },
    {
      key: 'refundee_name',
      label: t('transactions.table.refundeeName'),
      sortable: true,
    },
    {
      key: 'amount',
      label: t('transactions.table.amount'),
      sortable: true,
      align: 'right',
      format: 'currency',
    },
    {
      key: 'transaction_type',
      label: t('transactions.table.transactionType'),
      render: (value) => {
        const transactionTypes = {
          [REFUND_METHOD.CHECK]: t('transactions.transactionTypes.check'),
          [REFUND_METHOD.PAYPAL]: t('transactions.transactionTypes.paypal'),
          [REFUND_METHOD.VENMO]: t('transactions.transactionTypes.venmo'),
          [REFUND_METHOD.GIFTCARD]: t('transactions.transactionTypes.giftCard'),
          [REFUND_METHOD.DONATION]: t('transactions.transactionTypes.donation'),
          [REFUND_METHOD.ACH]: t('transactions.transactionTypes.ach'),
          [REFUND_METHOD.TRANSFER_BACK]: t(
            'transactions.transactionTypes.transferBack',
          ),
          [REFUND_METHOD.CANCEL]: t('transactions.transactionTypes.cancel'),
        };
        return (
          <Typography variant='body2'>
            {transactionTypes[value] || value}
          </Typography>
        );
      },
    },
    {
      key: 'transaction_status',
      label: t('transactions.table.status'),
      sortable: true,
      render: (value, row) => {
        return (
          <TransactionStatusChip status={value} voidSent={row.void_sent} />
        );
      },
    },
    {
      key: 'original_transaction_date',
      label: t('transactions.table.transactionDate'),
      sortable: true,
      format: 'date',
    },
    {
      key: 'return_date',
      label: t('transactions.table.returnDate'),
      sortable: true,
      format: 'date',
    },
    {
      key: 'original_transaction_id',
      label: t('transactions.table.originalTransactionId'),
      render: (value) => (
        <TransactionIdColumn
          value={value}
          enableNavigation={true}
          enableCopy={true}
        />
      ),
    },
    {
      key: 'external_id',
      label: t('transactions.table.transferId'),
      sortable: false,
      render: (value) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EllipsisText
            text={value || '–'}
            variant='body2'
            maxWidth={100}
            sx={{ fontFamily: 'monospace' }}
          />
          {value && <CopyButton text={value} />}
        </Box>
      ),
    },
    {
      key: 'return_code',
      label: t('transactions.table.returnCode'),
      sortable: false,
      render: (value) => (
        <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      key: 'return_reason',
      label: t('transactions.table.returnReason'),
      sortable: false,
      render: (value) => (
        <EllipsisText text={value || '–'} variant='body2' maxWidth={200} />
      ),
    },
    {
      key: 'return_description',
      label: t('transactions.table.returnDescription'),
      sortable: false,
      render: (value) => (
        <EllipsisText text={value || '–'} variant='body2' maxWidth={200} />
      ),
    },
    {
      key: 'transfer_back_id',
      label: t('transactions.table.transferBackId'),
      render: (value) => (
        <TransactionIdColumn
          value={value}
          enableNavigation={false}
          enableCopy={true}
        />
      ),
    },
    {
      key: 'actions',
      label: t('transactions.table.actions'),
      sortable: false,
      render: (_value, row, context = {}) => {
        const { togglePin, isPinned, refresh } = context;
        const isRowPinned = isPinned?.(row.id);

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip
              title={
                isRowPinned
                  ? t('transactions.actions.unpinTransfer')
                  : t('transactions.actions.pinTransfer')
              }>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  if (togglePin) {
                    togglePin(row);
                  }
                }}
                sx={{
                  color: isRowPinned ? 'warning.600' : 'action.disabled',
                }}>
                {isRowPinned ? (
                  <PinIcon sx={{ fontSize: '1rem' }} />
                ) : (
                  <PinOutlinedIcon sx={{ fontSize: '1rem' }} />
                )}
              </IconButton>
            </Tooltip>

            {row.can_create_transfer_back && (
              <Tooltip title={t('transactions.actions.createTransferBack')}>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTransferBackAction(row.id, false, refresh);
                  }}>
                  <UndoIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}

            {row.can_retry_transfer_back && (
              <Tooltip title={t('transactions.actions.retryTransferBack')}>
                <IconButton
                  size='small'
                  color='error'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTransferBackAction(row.id, true, refresh);
                  }}>
                  <RetryIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <PageLayout
      header={
        <CardHeader color='primary'>
          <Typography
            variant='h6'
            sx={{ color: 'white', fontWeight: 500 }}
            component='h4'>
            {t('transactions.title')}
          </Typography>
        </CardHeader>
      }>
      <DataTable
        columns={transationsColumns}
        endpoint='/transactions'
        initialSort={{ key: 'created_at', order: 'desc' }}
        pageSize={50}
        autoFetch={true}
        enableColumnPicker={true}
        enableSorting={true}
        enablePagination={false}
        enableFilters={true}
        enablePinning={true}
        persistenceKey='transfers'
        enableUrlPersistence={true}
        columnFilters={transfersColumnFilters}
        externalLoading={actionLoading}
        renderEmptyState={() => (
          <tr>
            <td colSpan={10} style={{ textAlign: 'center', padding: '3rem' }}>
              <Typography variant='h6' color='text.secondary'>
                {t('transactions.table.noTransfersFound')}
              </Typography>
            </td>
          </tr>
        )}
      />
    </PageLayout>
  );
};

export default Transactions;
